import type { CSSProperties } from 'react'
import { cn } from '@/lib/cn'
import { BRAND_MARK } from './brandMark'

export interface TrimetricMarkProps {
  /** Rendered size in px (square). */
  size?: number
  /** Bars draw in left to right on mount. Off by default; use once per view at most. */
  animate?: boolean
  className?: string
  /** Accessible name. Omit when a wordmark sits next to the mark. */
  title?: string
}

const BARS = [
  { d: 'M124 344H196', color: BRAND_MARK.swim },
  { d: 'M196 256H332', color: BRAND_MARK.bike },
  { d: 'M284 168H388', color: BRAND_MARK.run },
] as const

export function TrimetricMark({ size = 28, animate, className, title }: TrimetricMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      className={cn('shrink-0', className)}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <rect width="512" height="512" rx="120" fill={BRAND_MARK.tile} />
      <rect
        x="0.5"
        y="0.5"
        width="511"
        height="511"
        rx="119.5"
        fill="none"
        stroke="rgb(255 255 255 / 0.08)"
      />
      <g strokeLinecap="round" strokeWidth="60" fill="none">
        {BARS.map((bar, i) => (
          <path
            key={bar.d}
            d={bar.d}
            stroke={bar.color}
            pathLength={1}
            className={animate ? 'gd-draw' : undefined}
            style={animate ? ({ '--gd-delay': `${120 + i * 110}ms` } as CSSProperties) : undefined}
          />
        ))}
      </g>
    </svg>
  )
}

export interface WordmarkProps {
  className?: string
}

/** "Trimetric" set in Inter at a heavy weight with tight tracking. Inherits size and colour. */
export function Wordmark({ className }: WordmarkProps) {
  return (
    <span className={cn('font-display font-bold tracking-[-0.035em] text-ink', className)}>
      Trimetric
    </span>
  )
}

export interface LogoProps {
  /** Mark size in px; the wordmark scales with the surrounding font size. */
  size?: number
  animate?: boolean
  className?: string
}

/** Mark plus wordmark, for the nav rail, sign-in and onboarding screens. */
export function Logo({ size = 28, animate, className }: LogoProps) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <TrimetricMark size={size} animate={animate} />
      <Wordmark />
    </span>
  )
}
